import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiXMark,
  HiChevronLeft,
  HiChevronRight,
  HiArrowUpTray,
  HiDocument,
  HiTrash,
  HiCheckCircle,
} from 'react-icons/hi2';
import { auth, firestore, storage } from '../../database/firebase';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import toast from 'react-hot-toast';

const steps = [
  {
    id: 'company-details',
    title: 'Company Details',
    subtitle: 'Tell us about your organization',
    fields: [
      { name: 'companyName', label: 'Company Name', type: 'text', required: true },
      { name: 'registrationNumber', label: 'Registration Number', type: 'text', required: true },
      { name: 'vatNumber', label: 'VAT Number', type: 'text', required: false },
      { name: 'physicalAddress', label: 'Physical Address', type: 'text', required: true },
      { name: 'postalAddress', label: 'Postal Address', type: 'text', required: true },
      { name: 'contactPerson', label: 'Contact Person', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'phone', label: 'Phone', type: 'tel', required: true },
    ],
  },
  {
    id: 'project-summary',
    title: 'Project Summary',
    subtitle: 'Describe your innovative project',
    fields: [
      { name: 'projectTitle', label: 'Project Title', type: 'text', required: true },
      {
        name: 'projectDescription',
        label: 'Project Description',
        type: 'textarea',
        required: true,
      },
      {
        name: 'projectDuration',
        label: 'Project Duration (months)',
        type: 'number',
        required: true,
      },
      { name: 'projectStartDate', label: 'Project Start Date', type: 'date', required: true },
      { name: 'projectEndDate', label: 'Project End Date', type: 'date', required: true },
    ],
  },
  {
    id: 'financials-budget',
    title: 'Financials & Budget',
    subtitle: 'Financial planning and requirements',
    fields: [
      { name: 'totalBudget', label: 'Total Budget (BWP)', type: 'number', required: true },
      { name: 'requestedAmount', label: 'Amount Requested (BWP)', type: 'number', required: true },
      { name: 'ownContribution', label: 'Own Contribution (BWP)', type: 'number', required: true },
      { name: 'otherFunding', label: 'Other Funding Sources', type: 'textarea', required: false },
      { name: 'budgetBreakdown', label: 'Budget Breakdown', type: 'textarea', required: true },
    ],
  },
  {
    id: 'impact-innovation',
    title: 'Impact & Innovation',
    subtitle: "Demonstrate your project's value",
    fields: [
      {
        name: 'innovationDescription',
        label: 'Innovation Description',
        type: 'textarea',
        required: true,
      },
      { name: 'marketPotential', label: 'Market Potential', type: 'textarea', required: true },
      { name: 'socialImpact', label: 'Social Impact', type: 'textarea', required: true },
      {
        name: 'environmentalImpact',
        label: 'Environmental Impact',
        type: 'textarea',
        required: true,
      },
      { name: 'jobCreation', label: 'Expected Job Creation', type: 'number', required: true },
    ],
  },
  {
    id: 'documents',
    title: 'Supporting Documents',
    subtitle: 'Upload required documentation',
    fields: [
      {
        name: 'companyRegistration',
        label: 'Company Registration Document',
        type: 'file',
        required: true,
      },
      { name: 'taxClearance', label: 'Tax Clearance Certificate', type: 'file', required: true },
      { name: 'bankingSlips', label: 'Banking Slips', type: 'file', required: true },
      { name: 'quotations', label: 'Quotations', type: 'file', required: true },
      {
        name: 'additionalDocuments',
        label: 'Additional Supporting Documents',
        type: 'file',
        required: false,
      },
    ],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
};

const stepTransition = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    x: -50,
    transition: { duration: 0.3, ease: 'easeIn' },
  },
};

const BIFApplicationForm = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleFileChange = async e => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      setUploadProgress(prev => ({ ...prev, [name]: 0 }));

      try {
        const storageRef = ref(
          storage,
          `bif-applications/${auth.currentUser.uid}/${name}/${file.name}`
        );

        const snapshot = await uploadBytes(storageRef, file);

        const downloadURL = await getDownloadURL(snapshot.ref);
        setUploadedFiles(prev => ({
          ...prev,
          [name]: {
            name: file.name,
            url: downloadURL,
            type: file.type,
            size: file.size,
          },
        }));

        setUploadProgress(prev => ({ ...prev, [name]: 100 }));
      } catch (error) {
        toast.error('Error uploading file. Please try again.');
        setUploadProgress(prev => ({ ...prev, [name]: 0 }));
      }
    }
  };

  const removeFile = name => {
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[name];
      return newFiles;
    });
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[name];
      return newProgress;
    });
  };

  const validateStep = () => {
    const currentFields = steps[currentStep].fields;
    const newErrors = {};

    currentFields.forEach(field => {
      if (field.required) {
        if (field.type === 'file') {
          if (!uploadedFiles[field.name]) {
            newErrors[field.name] = 'This file is required';
          }
        } else if (!formData[field.name]) {
          newErrors[field.name] = 'This field is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const resetForm = () => {
    setCurrentStep(0);
    setFormData({});
    setErrors({});
    setUploadProgress({});
    setUploadedFiles({});
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setUploading(true);
    try {
      const applicationData = {
        ...formData,
        files: uploadedFiles,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const docRef = await addDoc(collection(firestore, 'bif_applications'), applicationData);

      const userRef = doc(firestore, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        bif_status: 'applied',
        bif_application_id: docRef.id,
      });

      toast.success('Application submitted successfully!', {
        duration: 4000,
        position: 'top-center',
        style: {
          background: '#374151',
          color: '#fff',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
        },
      });

      resetForm();
      toast.success('Application submitted successfully!', {
        duration: 4000,
        position: 'top-center',
        style: {
          background: '#374151',
          color: '#fff',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
        },
      });

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      toast.error('Error submitting application. Please try again.', {
        duration: 4000,
        position: 'top-center',
        style: {
          background: '#EF4444',
          color: '#fff',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
        },
      });
    } finally {
      setUploading(false);
    }
  };

  const renderField = field => {
    const commonClasses =
      'w-full rounded-xl border border-gray-200 px-4 py-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all duration-200 bg-white/70 backdrop-blur-sm';
    const errorClasses = errors[field.name]
      ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10 bg-red-50/50'
      : '';

    if (field.type === 'file') {
      return (
        <motion.div
          className="space-y-3"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="relative">
            <label className="group relative block">
              <input
                type="file"
                name={field.name}
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              <motion.div
                className={`flex cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-200 p-3 text-xs transition-all duration-300 group-hover:border-blue-400 group-hover:bg-blue-50/30 md:p-4 md:text-sm ${errorClasses} ${uploadedFiles[field.name] ? 'border-blue-300 bg-blue-50/30' : ''}`}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {uploadedFiles[field.name] ? (
                  <HiCheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <HiArrowUpTray className="h-5 w-5 text-gray-400 transition-colors group-hover:text-blue-500" />
                )}
                <span
                  className={`font-medium ${uploadedFiles[field.name] ? 'text-blue-700' : 'text-gray-600 group-hover:text-blue-600'} transition-colors`}
                >
                  {uploadedFiles[field.name]
                    ? 'File uploaded successfully'
                    : `Upload ${field.label}`}
                </span>
              </motion.div>
            </label>
          </div>

          <AnimatePresence mode="wait">
            {uploadedFiles[field.name] && (
              <motion.div
                initial={{ opacity: 0, y: -10, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -10, height: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="overflow-hidden"
              >
                <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                    >
                      <HiDocument className="h-4 w-4 text-blue-500" />
                    </motion.div>
                    <div>
                      <p className="max-w-[60vw] truncate text-xs font-medium text-black sm:max-w-[150px] md:text-sm">
                        {uploadedFiles[field.name].name}
                      </p>
                      <p className="text-[10px] text-gray-500 md:text-xs">
                        {(uploadedFiles[field.name].size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <motion.button
                    onClick={() => removeFile(field.name)}
                    className="rounded-lg p-1.5 text-gray-400 transition-all duration-200 hover:bg-red-50 hover:text-red-500"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <HiTrash className="h-4 w-4" />
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {uploadProgress[field.name] > 0 && uploadProgress[field.name] < 100 && (
              <motion.div
                initial={{ opacity: 0, scaleX: 0 }}
                animate={{ opacity: 1, scaleX: 1 }}
                exit={{ opacity: 0, scaleX: 0 }}
                className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress[field.name]}%` }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <motion.textarea
          name={field.name}
          value={formData[field.name] || ''}
          onChange={handleInputChange}
          className={`${commonClasses} ${errorClasses} min-h-[120px] resize-y`}
          placeholder={`Enter ${field.label.toLowerCase()}...`}
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          whileFocus={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
        />
      );
    }

    return (
      <motion.input
        type={field.type}
        name={field.name}
        value={formData[field.name] || ''}
        onChange={handleInputChange}
        className={`${commonClasses} ${errorClasses}`}
        placeholder={`Enter ${field.label.toLowerCase()}...`}
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        whileFocus={{ scale: 1.01 }}
        transition={{ duration: 0.2 }}
      />
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="my-8 w-full max-w-2xl rounded-2xl border border-white/20 bg-white/95 shadow-2xl backdrop-blur-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 border-b border-gray-100/50 bg-white/95 p-6 backdrop-blur-xl">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="mb-1 text-xl font-bold text-black">{steps[currentStep].title}</h3>
                <p className="text-sm text-gray-600">{steps[currentStep].subtitle}</p>
              </motion.div>
              <motion.button
                onClick={onClose}
                className="absolute right-4 top-4 rounded-xl p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <HiXMark className="h-5 w-5" />
              </motion.button>
            </div>

            <div className="p-6">
              <motion.div
                className="mb-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="mb-4 flex items-center justify-between overflow-x-auto pb-2">
                  {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                      <div className="flex items-center gap-2">
                        <motion.div
                          className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-all duration-300 ${
                            index <= currentStep
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                              : 'bg-gray-100 text-gray-400'
                          }`}
                          whileHover={index <= currentStep ? { scale: 1.1 } : {}}
                          animate={
                            index === currentStep
                              ? {
                                  boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.15)',
                                }
                              : {}
                          }
                        >
                          {index + 1}
                        </motion.div>
                        <span
                          className={`hidden text-sm font-medium transition-colors duration-300 sm:block ${
                            index <= currentStep ? 'text-black' : 'text-gray-400'
                          }`}
                        >
                          {step.title}
                        </span>
                      </div>
                      {index < steps.length - 1 && (
                        <motion.div
                          className={`mx-2 h-1 flex-1 rounded-full transition-all duration-500 ${
                            index < currentStep
                              ? 'bg-gradient-to-r from-blue-500 to-blue-600'
                              : 'bg-gray-200'
                          }`}
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: index < currentStep ? 1 : 0.3 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </motion.div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  variants={stepTransition}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="space-y-4"
                >
                  {steps[currentStep].id === 'documents' ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {steps[currentStep].fields.map((field, index) => (
                        <motion.div
                          key={field.name}
                          variants={itemVariants}
                          initial="hidden"
                          animate="visible"
                          transition={{ delay: index * 0.1 }}
                        >
                          <label className="mb-2 block text-sm font-semibold text-gray-700">
                            {field.label}
                            {field.required && <span className="ml-1 text-red-500">*</span>}
                          </label>
                          {renderField(field)}
                          <AnimatePresence>
                            {errors[field.name] && (
                              <motion.p
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -5 }}
                                className="mt-2 flex items-center gap-1 text-sm text-red-500"
                              >
                                <motion.span
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: 'spring', stiffness: 300 }}
                                >
                                  ⚠️
                                </motion.span>
                                {errors[field.name]}
                              </motion.p>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    steps[currentStep].fields.map((field, index) => (
                      <motion.div
                        key={field.name}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: index * 0.1 }}
                      >
                        <label className="mb-2 block text-sm font-semibold text-gray-700">
                          {field.label}
                          {field.required && <span className="ml-1 text-red-500">*</span>}
                        </label>
                        {renderField(field)}
                        <AnimatePresence>
                          {errors[field.name] && (
                            <motion.p
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -5 }}
                              className="mt-2 flex items-center gap-1 text-sm text-red-500"
                            >
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 300 }}
                              >
                                ⚠️
                              </motion.span>
                              {errors[field.name]}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))
                  )}
                </motion.div>
              </AnimatePresence>

              <motion.div
                className="mt-8 flex items-center justify-between"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <motion.button
                  onClick={handlePrevious}
                  disabled={currentStep === 0 || uploading}
                  className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200 ${
                    currentStep === 0 || uploading
                      ? 'cursor-not-allowed bg-gray-50 text-gray-400'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                  }`}
                  whileHover={currentStep > 0 && !uploading ? { scale: 1.05 } : {}}
                  whileTap={currentStep > 0 && !uploading ? { scale: 0.95 } : {}}
                >
                  <HiChevronLeft className="h-4 w-4" />
                  Previous
                </motion.button>

                <motion.button
                  onClick={handleNext}
                  disabled={uploading}
                  className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition-all duration-200 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl hover:shadow-blue-500/30 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-lg"
                  whileHover={!uploading ? { scale: 1.05, y: -1 } : {}}
                  whileTap={!uploading ? { scale: 0.95 } : {}}
                >
                  {uploading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                      />
                      Submitting...
                    </>
                  ) : (
                    <>
                      {currentStep === steps.length - 1 ? 'Submit Application' : 'Continue'}
                      {currentStep < steps.length - 1 && <HiChevronRight className="h-4 w-4" />}
                    </>
                  )}
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BIFApplicationForm;
